import React from 'react';
import { Box, Button, Group, Loader, Tooltip } from '@mantine/core';
import {
    IconEdit,
    IconTrash,
    IconCopy as IconClone,
    IconExternalLink,
    IconBinaryTree,
    IconEye,
} from '@tabler/icons-react';
import { EntityCategorizationService } from '../../../services/EntityCategorizationService';
import { isSet, isContent } from '../../../utils/entityTypeDetection';
import { isPluginFunctionEntityType } from '../../../utils/pluginFunctionUtils';
import { NON_INDEXED_PROPERTY_STUB } from '../../../utils/entityPropertyUtils';
import {
    canDeleteFromTargetResponse,
    canCreateFromTargetResponse,
    canApproveRequestFromTargetResponse,
    hasAnyWritableAttribute,
} from '../../../types/EntityEntitlements';
import type { EntityEntitlementForTargetResponse } from '../../../types/EntityEntitlements';

export interface EntityDetailActionBarProps {
    position?: 'top' | 'bottom';
    entityType: string;
    entity: any;
    fullEntity: any;
    targetEntitlement: EntityEntitlementForTargetResponse | null | undefined;
    isDeleting: boolean;
    loadingAttribute: string | null;
    isPivotLoading: boolean;
    loadNonIndexedAttribute: (key: string) => Promise<any>;
    setDeleteDialogOpen: (open: boolean) => void;
    handlePluginFunctionPivot: () => void;
    onEdit?: (entity: any, targetEntitlement?: EntityEntitlementForTargetResponse | null) => void;
    onOpenNewUi?: (entity: any, targetEntitlement?: EntityEntitlementForTargetResponse | null) => void;
    onDelete?: (entity: any) => Promise<void> | void;
    onClone?: (entity: any) => void;
    onClose: () => void;
    onRequestApprovalDecision: (decision: 'Approved' | 'Denied') => void;
    onPreviewMembers: (config: {
        filter: string;
        scope: string;
        titleOverride?: string;
        noteMessage?: string;
        attributes?: string[];
    }) => void;
}

const EntityDetailActionButtons: React.FC<Omit<EntityDetailActionBarProps, 'position'>> = ({
    entityType,
    entity,
    fullEntity,
    targetEntitlement,
    isDeleting,
    loadingAttribute,
    isPivotLoading,
    loadNonIndexedAttribute,
    setDeleteDialogOpen,
    handlePluginFunctionPivot,
    onEdit,
    onOpenNewUi: _onOpenNewUi,
    onDelete,
    onClone,
    onClose,
    onRequestApprovalDecision,
    onPreviewMembers,
}) => (
    <>
        {entityType?.toLowerCase() === 'requestapproval' && (fullEntity || entity) && (() => {
            const hasTier3 = targetEntitlement != null;
            const canApprove = canApproveRequestFromTargetResponse(targetEntitlement ?? null);
            const canDeny = canApprove;
            const approveButton = (
                <Button
                    variant="outline"
                    disabled={!canApprove}
                    onClick={() => {
                        if (!canApprove) return;
                        onRequestApprovalDecision('Approved');
                    }}
                >
                    Approve
                </Button>
            );
            const denyButton = (
                <Button
                    variant="outline"
                    color="red"
                    disabled={!canDeny}
                    onClick={() => {
                        if (!canDeny) return;
                        onRequestApprovalDecision('Denied');
                    }}
                >
                    Deny
                </Button>
            );

            if (canApprove) {
                return (
                    <>
                        {approveButton}
                        {denyButton}
                    </>
                );
            }

            const msg = hasTier3
                ? "You aren't authorized to decide this request."
                : "Decision permission hasn't loaded yet (tier3).";
            return (
                <Tooltip label={msg}>
                    <Box display="inline-block">
                        {approveButton}
                        {denyButton}
                    </Box>
                </Tooltip>
            );
        })()}
        {onDelete && entityType?.toLowerCase() !== 'journalentry' && (() => {
            const canDelete = canDeleteFromTargetResponse(targetEntitlement ?? null);
            const deleteButton = (
                <Button
                    color="red"
                    disabled={isDeleting || !canDelete}
                    leftSection={<IconTrash size={16} />}
                    onClick={() => canDelete && setDeleteDialogOpen(true)}
                >
                    Delete
                </Button>
            );
            return canDelete ? deleteButton : (
                <Tooltip label="You don't have permission to delete this entity.">
                    <Box display="inline-block">
                        {deleteButton}
                    </Box>
                </Tooltip>
            );
        })()}
        {onClone && (
            (() => {
                const canClone = canCreateFromTargetResponse(targetEntitlement ?? null);
                const isSystemSet = isSet(entityType) && fullEntity && (() => {
                    const raw = (fullEntity as any).IsSystem;
                    return typeof raw === 'boolean' ? raw : (typeof raw === 'string' ? raw.toLowerCase() === 'true' : !!raw);
                })();
                return (
                    <Tooltip label={!canClone ? "You don't have permission to create (clone) this entity type." : isSystemSet ? "System sets cannot be cloned." : "Create a copy of this entity"}>
                        <Box display="inline-block">
                            <Button
                                variant="outline"
                                leftSection={<IconClone size={16} />}
                                disabled={!canClone || isSystemSet}
                                onClick={() => {
                                    if (fullEntity) {
                                        onClone(fullEntity);
                                    } else if (entity) {
                                        onClone(entity);
                                    }
                                }}
                            >
                                Clone
                            </Button>
                        </Box>
                    </Tooltip>
                );
            })()
        )}
        {isPluginFunctionEntityType(entityType) && (fullEntity || entity) && (
            <Tooltip label="Call $pivot endpoint for this plug-in function">
                <Button
                    variant="outline"
                    leftSection={isPivotLoading ? <Loader size={16} /> : <IconBinaryTree size={16} />}
                    disabled={isPivotLoading}
                    onClick={handlePluginFunctionPivot}
                >
                    $ Pivot
                </Button>
            </Tooltip>
        )}
        {onEdit && entityType?.toLowerCase() !== 'journalentry' && (() => {
            const canEdit = hasAnyWritableAttribute(targetEntitlement ?? null);
            const displayEntity = fullEntity || entity;
            const bodyVal = displayEntity?.Body ?? displayEntity?.body;
            const isContentType = isContent(entityType);
            const bodyNeedsLoad = bodyVal === NON_INDEXED_PROPERTY_STUB || bodyVal === undefined;
            const shouldLoadBodyBeforeEdit = isContentType && bodyNeedsLoad;
            const editButton = (
                <Button
                    variant="filled"
                    leftSection={loadingAttribute === 'Body' ? <Loader size={16} color="white" /> : <IconEdit size={16} />}
                    disabled={!canEdit || loadingAttribute === 'Body'}
                    onClick={async () => {
                        if (!canEdit) return;
                        let entityToEdit = fullEntity || entity;
                        if (shouldLoadBodyBeforeEdit) {
                            const loaded = await loadNonIndexedAttribute('Body');
                            if (loaded) entityToEdit = loaded;
                        }
                        onEdit(entityToEdit, targetEntitlement ?? undefined);
                    }}
                >
                    Edit
                </Button>
            );
            return canEdit ? editButton : (
                <Tooltip label="You don't have permission to edit any attributes on this entity.">
                    <Box display="inline-block">
                        {editButton}
                    </Box>
                </Tooltip>
            );
        })()}
        {entityType?.toLowerCase() === 'journalentry' && (fullEntity || entity) && (() => {
            const targetId = (fullEntity || entity)?.TargetId ?? (fullEntity || entity)?.targetId;
            const targetType = (fullEntity || entity)?.TargetType ?? (fullEntity || entity)?.targetType;
            if (!targetId || !targetType) return null;
            const normalizedType = String(targetType).toLowerCase();
            return (
                <Button
                    variant="outline"
                    leftSection={<IconExternalLink size={16} />}
                    onClick={async () => {
                        try {
                            const categorizationService = EntityCategorizationService.getInstance();
                            const category = await categorizationService.getEntityCategory(normalizedType);
                            let route: string;
                            if (normalizedType === 'group') {
                                route = `#groups/view/${targetId}`;
                            } else if (normalizedType === 'class') {
                                route = `#classes/view/${targetId}`;
                            } else if (['school', 'building', 'responsibilitycenter'].includes(normalizedType)) {
                                route = `#localities/${normalizedType}/view/${targetId}`;
                            } else if (category.type === 'person') {
                                route = `#Person/${normalizedType}/view/${targetId}`;
                            } else if (category.type === 'locality') {
                                route = `#localities/${normalizedType}/view/${targetId}`;
                            } else if (category.type === 'admin') {
                                route = `#admin/${normalizedType}/view/${targetId}`;
                            } else {
                                route = `#other/${normalizedType}/view/${targetId}`;
                            }
                            onClose();
                            window.location.hash = route;
                        } catch (err) {
                            console.error('Failed to navigate to target entity:', err);
                        }
                    }}
                >
                    Open entity
                </Button>
            );
        })()}
        {!onEdit && fullEntity && fullEntity.Id && (() => {
            const entityId = fullEntity.Id || fullEntity.id;
            const normalizedEntityType = entityType.toLowerCase();
            const currentHash = window.location.hash;

            let isInFullView = false;
            if (normalizedEntityType === 'group') {
                isInFullView = currentHash === `#groups/view/${entityId}` || currentHash === `#groups/group/${entityId}`;
            } else if (normalizedEntityType === 'class') {
                isInFullView = currentHash === `#classes/view/${entityId}`;
            } else if (['school', 'building', 'responsibilitycenter'].includes(normalizedEntityType)) {
                isInFullView = currentHash === `#localities/${normalizedEntityType}/view/${entityId}`;
            }

            if (isInFullView) {
                return null;
            }

            return (
                <Button
                    variant="outline"
                    leftSection={<IconExternalLink size={16} />}
                    onClick={async () => {
                        try {
                            if (normalizedEntityType === 'group') {
                                const newHash = `#groups/view/${entityId}`;
                                onClose();
                                window.location.hash = newHash;
                                return;
                            } else if (normalizedEntityType === 'class') {
                                const newHash = `#classes/view/${entityId}`;
                                onClose();
                                window.location.hash = newHash;
                                return;
                            }

                            const categorizationService = EntityCategorizationService.getInstance();
                            const category = await categorizationService.getEntityCategory(normalizedEntityType);

                            let route: string;
                            if (category.type === 'person') {
                                route = `#Person/${normalizedEntityType}/view/${entityId}`;
                            } else if (category.type === 'locality') {
                                route = `#localities/${normalizedEntityType}/view/${entityId}`;
                            } else if (category.type === 'admin') {
                                route = `#admin/${normalizedEntityType}/view/${entityId}`;
                            } else {
                                route = `#other/${normalizedEntityType}/view/${entityId}`;
                            }

                            onClose();
                            window.location.hash = route;
                        } catch (error) {
                            console.error('Failed to navigate to full view:', error);
                        }
                    }}
                >
                    Open Full View
                </Button>
            );
        })()}
        {entityType?.toLowerCase() === 'group' && (fullEntity || entity) && (() => {
            const displayEntity = fullEntity || entity;
            const filterScopeVal = displayEntity?.FilterScope ?? displayEntity?.filterScope ?? '';
            const filterScopeString = typeof filterScopeVal === 'string'
                ? filterScopeVal.trim()
                : (filterScopeVal && typeof filterScopeVal === 'object'
                    ? String((filterScopeVal as any).Id ?? (filterScopeVal as any).id ?? (filterScopeVal as any).DisplayName ?? (filterScopeVal as any).displayName ?? '').trim()
                    : '').trim();
            const managedTypeVal = (displayEntity?.ManagedType ?? displayEntity?.managedType ?? '').toString().toLowerCase();
            const isEnumeratedManaged = managedTypeVal === 'enumerated';
            if (!filterScopeString || !isEnumeratedManaged) return null;
            const filterVal = displayEntity?.Filter ?? displayEntity?.filter ?? '';
            const filterTrimmed = (typeof filterVal === 'string' ? filterVal : String(filterVal ?? '')).trim();
            const effectiveFilter = filterTrimmed ? `(${filterTrimmed}) and OnPremId pr` : 'OnPremId pr';
            return (
                <Button
                    variant="outline"
                    leftSection={<IconEye size={16} />}
                    onClick={() => {
                        onPreviewMembers({
                            filter: effectiveFilter,
                            scope: filterScopeString,
                            titleOverride: 'Preview Members',
                            noteMessage: 'Group members must have OnPremId for previewing. The preview filter includes OnPremId pr.',
                            attributes: ['Id', 'DisplayName', 'EntityType', 'OnPremId'],
                        });
                    }}
                >
                    Preview Members
                </Button>
            );
        })()}
        <Button variant="default" onClick={onClose}>Close</Button>
    </>
);

export const EntityDetailActionBar: React.FC<EntityDetailActionBarProps> = ({
    position = 'bottom',
    ...buttonProps
}) => (
    <Box
        px="md"
        py="sm"
        style={position === 'top'
            ? { borderBottom: '1px solid var(--mantine-color-default-border)' }
            : { borderTop: '1px solid var(--mantine-color-default-border)' }}
    >
        <Group justify="flex-end" gap="sm">
            <EntityDetailActionButtons {...buttonProps} />
        </Group>
    </Box>
);
